import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

const QUICK_ACTIONS = [
  { key: "reseed_demo",        label: "Reseed Demo Data",     hint: "Idempotent demo seed across all fabrics" },
  { key: "dispatch_test",      label: "Test Notifications",   hint: "Fans out a test broadcast (silent if no channels configured)" },
  { key: "export_audit_csv",   label: "Export Audit (CSV)",   hint: "Download admin audit log as CSV" },
];

function sevColor(sev) {
  return { LOW: "#7AC860", MEDIUM: "#F5C842", HIGH: "#E8A020", EXTREME: "#C84A3A" }[sev] || "#60B8E8";
}
function statusColor(st) {
  return { OPTIMAL: "#7AC860", HEALTHY: "#7AC860", DEGRADED: "#F5C842", AT_RISK: "#E8A020", CRITICAL: "#C84A3A" }[st] || "#60B8E8";
}

export default function AdminOps() {
  const [exec, setExec] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [services, setServices] = useState({ total: 0, top: [], bottom: [] });
  const [query, setQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [toast, setToast] = useState("");

  const refresh = async () => {
    try {
      const [e, a, s] = await Promise.all([
        adminApi.opsExecutive(),
        adminApi.opsAlerts(20),
        adminApi.servicesSummary(),
      ]);
      setExec(e.data || null);
      setAlerts(a.data?.alerts || []);
      setServices(s.data || { total: 0, top: [], bottom: [] });
    } catch (err) { /* silent */ }
  };

  useEffect(() => { refresh(); const t = setInterval(refresh, 30000); return () => clearInterval(t); }, []);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!query || query.length < 2) { setSearchResult(null); return; }
    const { data } = await adminApi.opsSearch(query, 8);
    setSearchResult(data);
  };

  const runQuickAction = async (action) => {
    setBusy(action);
    setToast("");
    try {
      if (action === "export_audit_csv") {
        window.location.href = adminApi.auditCsvUrl();
        setToast("Audit CSV download started");
      } else {
        const { data } = await adminApi.opsQuickAction(action);
        setToast(`OK · ${action} · ${JSON.stringify(data).slice(0, 120)}`);
        await refresh();
      }
    } catch (err) {
      setToast(`FAIL · ${action} · ${err?.response?.status || ""}`);
    } finally {
      setBusy("");
      setTimeout(() => setToast(""), 6000);
    }
  };

  return (
    <div className="admin-page mesh-shell" data-testid="admin-ops">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />

      <div className="admin-page__head">
        <h2 className="admin-page__title">Operations Center</h2>
        <div className="mesh-eyebrow">v2.1 · Cross-service monitoring + quick actions</div>
      </div>

      {/* Executive Dashboard */}
      <section className="admin-section mesh-section" data-testid="ops-executive">
        <h3 className="admin-section__title">Executive Dashboard</h3>
        <div className="mesh-exec-grid">
          {[
            ["Open Signals", exec?.open_signals],
            ["High Signals", exec?.high_signals],
            ["Extreme Signals", exec?.extreme_signals, "#C84A3A"],
            ["Services Total", exec?.services_total],
            ["Services Critical", exec?.services_critical, "#C84A3A"],
            ["Services At-Risk", exec?.services_at_risk, "#E8A020"],
            ["Users", exec?.users_total],
            ["Sessions", exec?.sessions_total],
          ].map(([lbl, val, col]) => (
            <div key={lbl} className="mesh-exec-cell" data-testid={`ops-exec-${lbl.toLowerCase().replace(/ /g,'-')}`}>
              <div className="mesh-exec-cell__num" style={col ? { color: col } : {}}>{val ?? "—"}</div>
              <div className="mesh-exec-cell__lbl">{lbl}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Global Search */}
      <section className="admin-section mesh-section" data-testid="ops-search">
        <h3 className="admin-section__title">Global Search</h3>
        <form onSubmit={runSearch} className="mesh-search-form">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search signals, users, sessions, services (≥2 chars)"
            data-testid="ops-search-input"
            className="mesh-search-input"
          />
          <button type="submit" className="admin-linkbtn" data-testid="ops-search-submit">Search</button>
        </form>
        {searchResult && (
          <div className="mesh-search-result">
            <div className="mesh-search-col">
              <h4>Signals · {searchResult.signals?.length || 0}</h4>
              {(searchResult.signals || []).map((s) => (
                <div key={s.id} className="mesh-search-row">
                  <span className="mesh-pill" style={{ borderColor: sevColor(s.severity), color: sevColor(s.severity) }}>{s.severity}</span>
                  {s.asset && <span className="mesh-pill mesh-pill--gold">{s.asset}</span>}
                  <span className="mesh-search-row__title">{s.title}</span>
                </div>
              ))}
            </div>
            <div className="mesh-search-col">
              <h4>Services · {searchResult.services?.length || 0}</h4>
              {(searchResult.services || []).map((sv) => (
                <div key={sv.key} className="mesh-search-row">
                  <span className="mesh-pill" style={{ borderColor: statusColor(sv.status), color: statusColor(sv.status) }}>{sv.status}</span>
                  <span className="mesh-search-row__title">{sv.name}</span>
                  <span className="mesh-pill mesh-pill--ghost">{sv.health_index}/100</span>
                </div>
              ))}
            </div>
            <div className="mesh-search-col">
              <h4>Users · {searchResult.users?.length || 0}</h4>
              {(searchResult.users || []).map((u) => (
                <div key={u.id} className="mesh-search-row">
                  <span className="mesh-pill mesh-pill--ghost">{u.role}</span>
                  <span className="mesh-search-row__title">{u.name || u.email}</span>
                </div>
              ))}
            </div>
            <div className="mesh-search-col">
              <h4>Sessions · {searchResult.sessions?.length || 0}</h4>
              {(searchResult.sessions || []).map((s) => (
                <div key={s.id} className="mesh-search-row">
                  <span className="mesh-pill mesh-pill--ghost">{s.active_mode}</span>
                  <span className="mesh-search-row__title">{s.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Quick Actions */}
      <section className="admin-section mesh-section" data-testid="ops-quick-actions">
        <h3 className="admin-section__title">Quick Actions</h3>
        <div className="mesh-quick-grid">
          {QUICK_ACTIONS.map((qa) => (
            <button
              key={qa.key}
              type="button"
              className="mesh-quick-card"
              disabled={busy === qa.key}
              onClick={() => runQuickAction(qa.key)}
              data-testid={`ops-quick-${qa.key}`}
            >
              <span className="mesh-quick-label">{busy === qa.key ? "Running…" : qa.label}</span>
              <span className="mesh-quick-hint">{qa.hint}</span>
            </button>
          ))}
        </div>
        {toast && <div className="mesh-toast" data-testid="ops-toast">{toast}</div>}
      </section>

      {/* Live Alerts */}
      <section className="admin-section mesh-section" data-testid="ops-live-alerts">
        <h3 className="admin-section__title">Live Alerts</h3>
        <ol className="mesh-timeline">
          {alerts.map((a, i) => {
            const color = a.kind === "service" ? statusColor(a.status) : sevColor(a.severity);
            return (
              <li key={`${a.kind}-${a.id || i}`} className="mesh-timeline__item" style={{ borderColor: color }} data-testid={`ops-alert-${a.kind}-${i}`}>
                <span className="mesh-timeline__time">{a.created_at ? new Date(a.created_at).toLocaleTimeString() : "—"}</span>
                <span className="mesh-pill" style={{ borderColor: color, color }}>{(a.severity || a.status || a.kind).toString().toUpperCase()}</span>
                <span className="mesh-pill mesh-pill--ghost">{a.kind}</span>
                {a.asset && <span className="mesh-pill mesh-pill--gold">{a.asset}</span>}
                <span className="mesh-timeline__title">{a.title}</span>
                {typeof a.oracle_score === "number" && <span className="mesh-score mesh-score--inline"><span className="mesh-score__num">{a.oracle_score}</span></span>}
                {typeof a.health_index === "number" && <span className="mesh-pill mesh-pill--ghost">H {a.health_index}</span>}
              </li>
            );
          })}
          {alerts.length === 0 && <div className="admin-foot-note">No active alerts.</div>}
        </ol>
      </section>

      {/* Service Health Dashboard (compact) */}
      <section className="admin-section mesh-section" data-testid="ops-service-health">
        <h3 className="admin-section__title">Service Health Dashboard</h3>
        <div className="mesh-service-rollup">
          <div className="mesh-service-rollup__cell">
            <div className="mesh-exec-cell__num">{services.total}</div>
            <div className="mesh-exec-cell__lbl">Services</div>
          </div>
          <div className="mesh-service-rollup__cell">
            <div className="mesh-exec-cell__num">{services.avg_health || 0}</div>
            <div className="mesh-exec-cell__lbl">Avg Health</div>
          </div>
          <div className="mesh-service-rollup__cell">
            <div className="mesh-exec-cell__num" style={{ color: "#7AC860" }}>{services.healthy || 0}</div>
            <div className="mesh-exec-cell__lbl">Healthy</div>
          </div>
          <div className="mesh-service-rollup__cell">
            <div className="mesh-exec-cell__num" style={{ color: "#F5C842" }}>{services.degraded || 0}</div>
            <div className="mesh-exec-cell__lbl">Degraded</div>
          </div>
          <div className="mesh-service-rollup__cell">
            <div className="mesh-exec-cell__num" style={{ color: "#E8A020" }}>{services.at_risk || 0}</div>
            <div className="mesh-exec-cell__lbl">At-Risk</div>
          </div>
          <div className="mesh-service-rollup__cell">
            <div className="mesh-exec-cell__num" style={{ color: "#C84A3A" }}>{services.critical || 0}</div>
            <div className="mesh-exec-cell__lbl">Critical</div>
          </div>
        </div>
        <div className="mesh-svc-leader">
          <div>
            <h4 className="mesh-svc-leader__h">Top performers</h4>
            {(services.top || []).map((t) => (
              <div key={t.key} className="mesh-search-row">
                <span className="mesh-pill" style={{ borderColor: statusColor(t.status), color: statusColor(t.status) }}>{t.status}</span>
                <span className="mesh-search-row__title">{t.name}</span>
                <span className="mesh-pill mesh-pill--ghost">{t.health_index}/100</span>
              </div>
            ))}
          </div>
          <div>
            <h4 className="mesh-svc-leader__h">Watch list</h4>
            {(services.bottom || []).map((t) => (
              <div key={t.key} className="mesh-search-row">
                <span className="mesh-pill" style={{ borderColor: statusColor(t.status), color: statusColor(t.status) }}>{t.status}</span>
                <span className="mesh-search-row__title">{t.name}</span>
                <span className="mesh-pill mesh-pill--ghost">{t.health_index}/100</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
